import React, { useEffect, useState, useCallback } from 'react';
import {
  Plug, Plus, Trash2, Edit2, RefreshCw, FlaskConical, Activity, FileInput,
  Loader2, Copy, ExternalLink, Eye, EyeOff, X, CheckCircle2,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { punchout } from '../../lib/govApi';
import { toast } from '../../hooks/use-toast';

const emptyPartner = {
  name: '',
  from_domain: 'NetworkID',
  from_identity: '',
  to_domain: 'DUNS',
  to_identity: '',
  sender_domain: '',
  sender_identity: '',
  shared_secret: '',
  default_agency_id: '',
  is_active: true,
  notes: '',
  protocol: 'cxml',
  order_response_url: '',
  auto_send_confirmation: true,
  auto_send_ship_notice: true,
  poom_encoding: 'cxml-base64',
  mtls_enabled: false,
  mtls_cert_pem: '',
  mtls_key_pem: '',
};

const GovPunchoutAdmin = () => {
  const [partners, setPartners] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [inboundPOs, setInboundPOs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);   // partner object being edited or null
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(emptyPartner);
  const [showSecret, setShowSecret] = useState(false);
  const [testRunning, setTestRunning] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testPartnerId, setTestPartnerId] = useState('');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [p, s, po] = await Promise.all([
        punchout.listPartners(),
        punchout.listSessions(),
        punchout.listInboundPOs(),
      ]);
      setPartners(p.data || []);
      setSessions(s.data || []);
      setInboundPOs(po.data || []);
    } catch (e) {
      toast({ title: 'Load failed', description: e.response?.data?.detail || 'Could not load punchout data', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const startCreate = () => {
    setForm(emptyPartner);
    setEditing(null);
    setCreating(true);
    setShowSecret(false);
  };

  const startEdit = (p) => {
    setForm({
      name: p.name || '',
      from_domain: p.from_domain || '',
      from_identity: p.from_identity || '',
      to_domain: p.to_domain || '',
      to_identity: p.to_identity || '',
      sender_domain: p.sender_domain || '',
      sender_identity: p.sender_identity || '',
      shared_secret: '',     // require admin to retype on edit
      default_agency_id: p.default_agency_id || '',
      is_active: !!p.is_active,
      notes: p.notes || '',
      protocol: p.protocol || 'cxml',
      order_response_url: p.order_response_url || '',
      auto_send_confirmation: p.auto_send_confirmation !== false,
      auto_send_ship_notice: p.auto_send_ship_notice !== false,
      poom_encoding: p.poom_encoding || 'cxml-base64',
      mtls_enabled: !!p.mtls_enabled,
      mtls_cert_pem: '',       // require admin to repaste on edit
      mtls_key_pem: '',
    });
    setEditing(p);
    setCreating(true);
    setShowSecret(false);
  };

  const closeForm = () => { setCreating(false); setEditing(null); setForm(emptyPartner); };

  const submitForm = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        const patch = { ...form };
        if (!patch.shared_secret) delete patch.shared_secret;
        if (!patch.mtls_cert_pem) delete patch.mtls_cert_pem;
        if (!patch.mtls_key_pem) delete patch.mtls_key_pem;
        await punchout.updatePartner(editing.id, patch);
        toast({ title: 'Partner updated' });
      } else {
        const body = { ...form };
        if (!body.mtls_cert_pem) delete body.mtls_cert_pem;
        if (!body.mtls_key_pem) delete body.mtls_key_pem;
        await punchout.createPartner(body);
        toast({ title: 'Partner created' });
      }
      closeForm();
      fetchAll();
    } catch (err) {
      toast({ title: 'Save failed', description: err.response?.data?.detail || 'Could not save partner', variant: 'destructive' });
    }
  };

  const onDelete = async (p) => {
    if (!window.confirm(`Delete trading partner "${p.name}"? This cannot be undone.`)) return;
    try {
      await punchout.deletePartner(p.id);
      toast({ title: 'Partner deleted' });
      fetchAll();
    } catch (e) {
      toast({ title: 'Delete failed', description: e.response?.data?.detail || '', variant: 'destructive' });
    }
  };

  const runTest = async () => {
    if (!testPartnerId) return;
    setTestRunning(true);
    setTestResult(null);
    try {
      const res = await punchout.testSetup({ partner_id: testPartnerId, user_email: 'tester@example.gov' });
      setTestResult(res.data);
      if (res.data?.ok) toast({ title: 'cXML test passed', description: 'Click "Open Punchout Catalog" to walk the buyer flow.' });
      else toast({ title: 'cXML test failed', description: res.data?.error || 'Check credentials', variant: 'destructive' });
      fetchAll();
    } catch (e) {
      toast({ title: 'Tester error', description: e.response?.data?.detail || '', variant: 'destructive' });
    } finally {
      setTestRunning(false);
    }
  };

  const copyToClipboard = (text, label = 'Copied') => {
    navigator.clipboard.writeText(text || '').then(() => toast({ title: label }));
  };

  return (
    <div className="space-y-4" data-testid="punchout-admin-page">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 flex items-center gap-2">
            <Plug className="h-7 w-7 text-blue-700" /> Punchout / cXML Integration
          </h1>
          <p className="text-sm text-slate-500">Manage Ariba / Coupa / Jaggaer trading partners and inspect live sessions.</p>
        </div>
        <Button variant="outline" onClick={fetchAll} data-testid="punchout-refresh"><RefreshCw className="h-4 w-4 mr-1" /> Refresh</Button>
      </div>

      <Tabs defaultValue="partners" className="w-full">
        <TabsList>
          <TabsTrigger value="partners" data-testid="tab-partners"><Plug className="h-3.5 w-3.5 mr-1" /> Trading Partners</TabsTrigger>
          <TabsTrigger value="sessions" data-testid="tab-sessions"><Activity className="h-3.5 w-3.5 mr-1" /> Sessions</TabsTrigger>
          <TabsTrigger value="inbound" data-testid="tab-inbound"><FileInput className="h-3.5 w-3.5 mr-1" /> Inbound POs</TabsTrigger>
          <TabsTrigger value="tester" data-testid="tab-tester"><FlaskConical className="h-3.5 w-3.5 mr-1" /> cXML Tester</TabsTrigger>
        </TabsList>

        {/* PARTNERS */}
        <TabsContent value="partners" className="space-y-3">
          <div className="flex justify-end">
            <Button onClick={startCreate} className="bg-blue-700 hover:bg-blue-800 text-white" data-testid="punchout-add-partner">
              <Plus className="h-4 w-4 mr-1" /> Add Trading Partner
            </Button>
          </div>

          {loading ? (
            <div className="flex justify-center py-10"><Loader2 className="h-8 w-8 animate-spin text-blue-700" /></div>
          ) : partners.length === 0 ? (
            <Card className="border-2 border-dashed">
              <CardContent className="p-10 text-center text-slate-500">No trading partners configured yet.</CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {partners.map(p => (
                <Card key={p.id} className="border-2" data-testid={`punchout-partner-${p.id}`}>
                  <CardContent className="p-4 space-y-2">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-bold text-slate-800">{p.name}</h3>
                        <div className="text-[11px] uppercase tracking-wide text-slate-400">
                          {p.is_active ? <span className="text-emerald-700">● Active</span> : <span className="text-slate-400">● Inactive</span>}
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => startEdit(p)} data-testid={`punchout-edit-${p.id}`}><Edit2 className="h-3.5 w-3.5" /></Button>
                        <Button variant="ghost" size="sm" className="text-red-600" onClick={() => onDelete(p)} data-testid={`punchout-delete-${p.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                      </div>
                    </div>
                    <div className="text-xs text-slate-500 space-y-0.5">
                      <div><strong>Protocol:</strong> <span className="uppercase font-semibold text-blue-700">{p.protocol || 'cxml'}</span></div>
                      <div><strong>From:</strong> {p.from_domain} · {p.from_identity}</div>
                      <div><strong>To:</strong> {p.to_domain} · {p.to_identity}</div>
                      <div><strong>Shared secret:</strong> <code>{p.shared_secret_preview}</code> <span className="text-[10px] text-emerald-700">🔒 encrypted at rest</span></div>
                      {p.order_response_url && <div className="truncate"><strong>Order response URL:</strong> <code className="text-[10px]">{p.order_response_url}</code></div>}
                      <div className="text-[10px] text-slate-400">
                        Auto-confirm {p.auto_send_confirmation !== false ? '✓' : '✗'} ·
                        Auto-ASN {p.auto_send_ship_notice !== false ? '✓' : '✗'}
                      </div>
                      {p.notes && <div className="italic text-slate-400">{p.notes}</div>}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* SESSIONS */}
        <TabsContent value="sessions">
          <Card className="border-2">
            <CardHeader className="pb-2"><CardTitle className="text-base">Recent Punchout Sessions</CardTitle></CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                    <tr>
                      <th className="text-left p-3">Created</th>
                      <th className="text-left p-3">Partner</th>
                      <th className="text-left p-3">Buyer Cookie</th>
                      <th className="text-left p-3">User</th>
                      <th className="text-left p-3">Status</th>
                      <th className="text-right p-3">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sessions.length === 0 ? (
                      <tr><td colSpan={6} className="p-6 text-center text-slate-400">No sessions yet.</td></tr>
                    ) : sessions.map(s => (
                      <tr key={s.token} className="border-t" data-testid={`session-${s.token}`}>
                        <td className="p-3 text-xs">{s.created_at ? new Date(s.created_at).toLocaleString() : '—'}</td>
                        <td className="p-3">{s.partner_name || '—'} {s.is_test && <span className="ml-1 text-[10px] bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">TEST</span>}</td>
                        <td className="p-3 font-mono text-xs">{s.buyer_cookie}</td>
                        <td className="p-3 text-xs">{s.user_email || '—'}</td>
                        <td className="p-3">
                          {s.status === 'active' && <span className="text-emerald-700 text-xs font-semibold">● Active</span>}
                          {s.status === 'transferred' && <span className="text-blue-700 text-xs font-semibold">● Transferred</span>}
                          {s.status === 'expired' && <span className="text-slate-400 text-xs font-semibold">● Expired</span>}
                        </td>
                        <td className="p-3 text-right font-semibold">{s.total ? `$${Number(s.total).toFixed(2)}` : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* INBOUND POs */}
        <TabsContent value="inbound">
          <Card className="border-2">
            <CardHeader className="pb-2"><CardTitle className="text-base">Inbound Purchase Orders (cXML OrderRequest)</CardTitle></CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                    <tr>
                      <th className="text-left p-3">Received</th>
                      <th className="text-left p-3">Partner</th>
                      <th className="text-left p-3">Buyer PO</th>
                      <th className="text-left p-3">Gov Order</th>
                      <th className="text-right p-3">Items</th>
                      <th className="text-right p-3">Total</th>
                      <th className="text-left p-3">Unmatched SKUs</th>
                    </tr>
                  </thead>
                  <tbody>
                    {inboundPOs.length === 0 ? (
                      <tr><td colSpan={7} className="p-6 text-center text-slate-400">No inbound POs yet.</td></tr>
                    ) : inboundPOs.map((po, idx) => (
                      <tr key={idx} className="border-t" data-testid={`inbound-po-${po.gov_order_id}`}>
                        <td className="p-3 text-xs">{po.received_at ? new Date(po.received_at).toLocaleString() : '—'}</td>
                        <td className="p-3">{po.partner_name}</td>
                        <td className="p-3 font-mono text-xs">{po.buyer_po}</td>
                        <td className="p-3 font-mono text-xs">{po.gov_order_id}</td>
                        <td className="p-3 text-right">{po.item_count}</td>
                        <td className="p-3 text-right font-semibold">${Number(po.total).toFixed(2)}</td>
                        <td className="p-3 text-xs">
                          {po.unmatched_skus?.length ? (
                            <span className="text-amber-700">{po.unmatched_skus.join(', ')}</span>
                          ) : <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 inline" />}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* TESTER */}
        <TabsContent value="tester">
          <Card className="border-2">
            <CardHeader><CardTitle className="text-base">Test cXML Setup End-to-End</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-slate-500">
                Pick a partner and we&apos;ll generate a sample <code>PunchOutSetupRequest</code>, fire it against our own endpoint,
                and give you the <strong>StartPage URL</strong> the buyer&apos;s browser would be redirected to.
              </p>
              <div className="flex flex-wrap gap-2 items-end">
                <div className="flex-1 min-w-[220px]">
                  <Label htmlFor="test-partner">Trading partner</Label>
                  <select
                    id="test-partner"
                    className="w-full border-2 border-slate-200 rounded p-2 text-sm"
                    value={testPartnerId}
                    onChange={(e) => setTestPartnerId(e.target.value)}
                    data-testid="punchout-test-partner-select"
                  >
                    <option value="">Select a partner…</option>
                    {partners.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
                <Button onClick={runTest} disabled={!testPartnerId || testRunning} className="bg-blue-700 hover:bg-blue-800 text-white" data-testid="punchout-test-run">
                  {testRunning ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <FlaskConical className="h-4 w-4 mr-1" />}
                  Run SetupRequest
                </Button>
              </div>

              {testResult && (
                <div className="space-y-2 pt-2" data-testid="punchout-test-result">
                  {testResult.ok ? (
                    <div className="p-3 rounded bg-emerald-50 border border-emerald-200 text-sm flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-emerald-700" />
                      <span><strong>200 OK</strong> — session created. Click below to enter the punchout catalog.</span>
                    </div>
                  ) : (
                    <div className="p-3 rounded bg-red-50 border border-red-200 text-sm">
                      <strong>{testResult.error}</strong>
                    </div>
                  )}
                  {testResult.start_page_url && (
                    <div className="flex flex-wrap items-center gap-2">
                      <Button asChild className="bg-emerald-600 hover:bg-emerald-700 text-white">
                        <a href={testResult.start_page_url} target="_blank" rel="noreferrer" data-testid="punchout-test-open">
                          <ExternalLink className="h-4 w-4 mr-1" /> Open Punchout Catalog
                        </a>
                      </Button>
                      <Button variant="outline" onClick={() => copyToClipboard(testResult.start_page_url, 'Start URL copied')}>
                        <Copy className="h-3.5 w-3.5 mr-1" /> Copy URL
                      </Button>
                    </div>
                  )}
                  <details className="text-xs">
                    <summary className="cursor-pointer text-slate-600 font-semibold">Request cXML ▶</summary>
                    <pre className="mt-1 bg-slate-900 text-slate-100 p-3 rounded overflow-auto max-h-60 whitespace-pre-wrap">{testResult.request_xml}</pre>
                  </details>
                  {testResult.response_xml && (
                    <details className="text-xs">
                      <summary className="cursor-pointer text-slate-600 font-semibold">Response cXML ▶</summary>
                      <pre className="mt-1 bg-slate-900 text-slate-100 p-3 rounded overflow-auto max-h-60 whitespace-pre-wrap">{testResult.response_xml}</pre>
                    </details>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create / Edit dialog */}
      {creating && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={closeForm}>
          <Card className="w-full max-w-2xl bg-white" onClick={(e) => e.stopPropagation()} data-testid="punchout-partner-form">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
              <CardTitle className="text-base">{editing ? 'Edit' : 'Add'} Trading Partner</CardTitle>
              <Button variant="ghost" size="sm" onClick={closeForm}><X className="h-4 w-4" /></Button>
            </CardHeader>
            <CardContent>
              <form onSubmit={submitForm} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2">
                  <Label htmlFor="name">Partner name</Label>
                  <Input id="name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ariba — WA State" data-testid="partner-name-input" />
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="protocol">Protocol</Label>
                  <select
                    id="protocol"
                    className="w-full border-2 border-slate-200 rounded p-2 text-sm bg-white"
                    value={form.protocol}
                    onChange={(e) => setForm({ ...form, protocol: e.target.value })}
                    data-testid="partner-protocol-select"
                  >
                    <option value="cxml">cXML (Ariba / Coupa / Jaggaer / SciQuest)</option>
                    <option value="oci">OCI (SAP Open Catalog Interface)</option>
                  </select>
                </div>
                <div>
                  <Label htmlFor="fd">From domain</Label>
                  <Input id="fd" required value={form.from_domain} onChange={(e) => setForm({ ...form, from_domain: e.target.value })} placeholder="NetworkID" />
                </div>
                <div>
                  <Label htmlFor="fi">From identity</Label>
                  <Input id="fi" required value={form.from_identity} onChange={(e) => setForm({ ...form, from_identity: e.target.value })} placeholder="AN01000000123" />
                </div>
                <div>
                  <Label htmlFor="td">To domain</Label>
                  <Input id="td" required value={form.to_domain} onChange={(e) => setForm({ ...form, to_domain: e.target.value })} placeholder="DUNS" />
                </div>
                <div>
                  <Label htmlFor="ti">To identity</Label>
                  <Input id="ti" required value={form.to_identity} onChange={(e) => setForm({ ...form, to_identity: e.target.value })} placeholder="123456789" />
                </div>
                <div>
                  <Label htmlFor="sd">Sender domain (optional)</Label>
                  <Input id="sd" value={form.sender_domain} onChange={(e) => setForm({ ...form, sender_domain: e.target.value })} placeholder="defaults to From domain" />
                </div>
                <div>
                  <Label htmlFor="si">Sender identity (optional)</Label>
                  <Input id="si" value={form.sender_identity} onChange={(e) => setForm({ ...form, sender_identity: e.target.value })} placeholder="defaults to From identity" />
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="ss">Shared secret {editing && <span className="text-slate-400 text-[11px]">(leave blank to keep)</span>}</Label>
                  <div className="relative">
                    <Input id="ss" type={showSecret ? 'text' : 'password'} value={form.shared_secret} onChange={(e) => setForm({ ...form, shared_secret: e.target.value })} required={!editing} data-testid="partner-secret-input" />
                    <button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400" onClick={() => setShowSecret(v => !v)}>
                      {showSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="da">Default agency ID (optional)</Label>
                  <Input id="da" value={form.default_agency_id} onChange={(e) => setForm({ ...form, default_agency_id: e.target.value })} placeholder="Mongo agency _id this partner maps to" />
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="nt">Notes</Label>
                  <Textarea id="nt" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} />
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="ru">Order response URL <span className="text-slate-400 text-[11px]">(buyer endpoint that receives our ConfirmationRequest / ShipNoticeRequest)</span></Label>
                  <Input id="ru" type="url" value={form.order_response_url} onChange={(e) => setForm({ ...form, order_response_url: e.target.value })} placeholder="https://buyer.example.com/cxml/orderresponse" data-testid="partner-order-response-url" />
                </div>
                <div className="flex items-center gap-2">
                  <Switch id="auto-conf" checked={form.auto_send_confirmation} onCheckedChange={(v) => setForm({ ...form, auto_send_confirmation: v })} />
                  <Label htmlFor="auto-conf" className="cursor-pointer text-sm">Auto-send ConfirmationRequest</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch id="auto-asn" checked={form.auto_send_ship_notice} onCheckedChange={(v) => setForm({ ...form, auto_send_ship_notice: v })} />
                  <Label htmlFor="auto-asn" className="cursor-pointer text-sm">Auto-send ShipNoticeRequest</Label>
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="enc">POOM encoding</Label>
                  <select
                    id="enc"
                    className="w-full border-2 border-slate-200 rounded p-2 text-sm bg-white"
                    value={form.poom_encoding}
                    onChange={(e) => setForm({ ...form, poom_encoding: e.target.value })}
                    data-testid="partner-poom-encoding-select"
                  >
                    <option value="cxml-base64">cxml-base64 (default — Ariba, Coupa, Jaggaer)</option>
                    <option value="cxml-urlencoded">cxml-urlencoded (some Workday Strategic Sourcing tenants)</option>
                  </select>
                </div>
                <div className="sm:col-span-2 flex items-center gap-2">
                  <Switch id="mtls" checked={form.mtls_enabled} onCheckedChange={(v) => setForm({ ...form, mtls_enabled: v })} data-testid="partner-mtls-toggle" />
                  <Label htmlFor="mtls" className="cursor-pointer text-sm">Enable MutualTLS for outbound cXML</Label>
                </div>
                {form.mtls_enabled && (
                  <>
                    <div className="sm:col-span-2">
                      <Label htmlFor="cert">Client certificate (PEM)</Label>
                      <Textarea id="cert" rows={4} value={form.mtls_cert_pem} onChange={(e) => setForm({ ...form, mtls_cert_pem: e.target.value })} placeholder="-----BEGIN CERTIFICATE-----&#10;...&#10;-----END CERTIFICATE-----" data-testid="partner-mtls-cert" />
                    </div>
                    <div className="sm:col-span-2">
                      <Label htmlFor="key">Client private key (PEM) {editing && <span className="text-slate-400 text-[11px]">(leave blank to keep existing)</span>}</Label>
                      <Textarea id="key" rows={4} value={form.mtls_key_pem} onChange={(e) => setForm({ ...form, mtls_key_pem: e.target.value })} placeholder="-----BEGIN PRIVATE KEY-----&#10;...&#10;-----END PRIVATE KEY-----" data-testid="partner-mtls-key" />
                      <p className="text-[10px] text-slate-400 mt-1">🔒 Encrypted at rest with the same Fernet key as the shared secret.</p>
                    </div>
                  </>
                )}
                <div className="sm:col-span-2 flex items-center gap-2">
                  <Switch id="ia" checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} />
                  <Label htmlFor="ia" className="cursor-pointer">Active</Label>
                </div>
                <div className="sm:col-span-2 flex justify-end gap-2 pt-2">
                  <Button type="button" variant="outline" onClick={closeForm}>Cancel</Button>
                  <Button type="submit" className="bg-blue-700 hover:bg-blue-800 text-white" data-testid="partner-save-btn">{editing ? 'Save changes' : 'Create partner'}</Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default GovPunchoutAdmin;
